import React, { useEffect, useMemo, useState } from 'react';
import { Input as FormInput, Textarea as FormTextarea, Select as FormSelect, Checkbox as FormCheckbox } from '../FormField';
import TagMultiSelect from '../ui/TagMultiSelect';
import SearchableSelect from '../ui/SearchableSelect';
import Button from '../ui/Button';
import Icon from '../Icon';
import DuplicateContactWarning from './DuplicateContactWarning';
import GuestRelationsPanel from './GuestRelationsPanel';
import { useNotifications } from '../NotificationProvider';
import { useDuplicateContactWarning } from '../../hooks/useDuplicateContactWarning';
import { createGuest, updateGuest, loadGuests, loadGuestTags, refreshGuestTagsFromApi } from '../../services/GuestService';
import { normalizeGuestName } from '../../utils/guestName';
import { GUEST_ROLE_OPTIONS } from '../../utils/guestRoles';
import { buildSideOptions, ALL_SIDES, NO_SIDE } from '../../utils/guestSide';
import { readableErrorMessage } from '../../utils/permanentDeleteError';
import { DEFAULT_PLUS_ONE_RELATIONSHIP } from '../../types/Guests';
import type { Guest, GuestTag, PlusOne, RSVPStatus, GuestStatus } from '../../types/Guests';
import { recordId } from '../../utils/recordId';

const DIETARY_OPTIONS = ['Vegetarian', 'Vegan', 'Halal', 'Kosher', 'Gluten-free', 'Nut allergy', 'Dairy-free', 'Seafood allergy', 'No pork'];

const RSVP_OPTIONS: Array<{ value: RSVPStatus; label: string }> = [
  { value: 'pending', label: 'Pending response' },
  { value: 'confirmed', label: 'Confirmed attendance' },
  { value: 'declined', label: 'Declined invitation' },
];

// Only decides when to ask the server; the server does the comparing.
const looksLikeEmail = (value: string) => value.includes('@');
const looksLikePhone = (value: string) => value.replace(/\D/g, '').length >= 6;
const PHONE_HINT = '+ country code, then number';
const NEW_SIDE = '__new__';

interface GuestFormProps {
  /** The guest being edited; omitted to add a new one. */
  guest?: Guest;
  onSaved: (guest: Guest) => void;
  onCancel: () => void;
}

const idForPlusOne = () => `po-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

/**
 * Add or edit one guest. The name is tidied when the field is left (the API tidies it again on save, so a client that
 * skips this still gets a tidy name). A phone or email another guest already uses shows an inline warning once the
 * field is left; the warning never blocks Save, and the saved guest's own warnings are announced after the write.
 */
const GuestForm: React.FC<GuestFormProps> = ({ guest, onSaved, onCancel }) => {
  const { showError } = useNotifications();
  const isEditing = Boolean(guest);
  const [tagCatalog, setTagCatalog] = useState<GuestTag[]>(() => loadGuestTags());
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [form, setForm] = useState({
    name: guest?.name ?? '',
    email: guest?.email ?? '',
    phone: guest?.phone ?? '',
    side: guest?.side ?? '',
    role: guest?.role ?? '',
    is_honoree: guest?.is_honoree === true,
    tags: guest?.tags ?? ([] as GuestTag[]),
    rsvp_status: (guest?.rsvp_status ?? 'pending') as RSVPStatus,
    status: (guest?.status ?? 'active') as GuestStatus,
    dietary_restrictions: guest?.dietary_restrictions ?? ([] as string[]),
    notes: guest?.notes ?? '',
    plus_ones: guest?.plus_ones ?? ([] as PlusOne[]),
    party_of: guest?.party_of ?? '',
    party_role: guest?.party_role ?? '',
  });
  const [newPlusOneName, setNewPlusOneName] = useState('');
  const [customSide, setCustomSide] = useState('');

  useEffect(() => {
    let cancelled = false;
    refreshGuestTagsFromApi().then(tags => { if (!cancelled) setTagCatalog(tags); }).catch(() => { /* keep what is cached */ });
    return () => { cancelled = true; };
  }, []);

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setErrors(prev => { if (!(key in prev)) return prev; const { [key]: _gone, ...rest } = prev; return rest; });
  };

  const phoneWarning = useDuplicateContactWarning({ field: 'phone', value: form.phone, initialValue: guest?.phone, excludeGuest: guest?.id, isValid: looksLikePhone });
  const emailWarning = useDuplicateContactWarning({ field: 'email', value: form.email, initialValue: guest?.email, excludeGuest: guest?.id, isValid: looksLikeEmail });

  const others = useMemo(() => loadGuests().filter(g => g.id !== guest?.id), [guest?.id]);
  const partyOfOptions = useMemo(
    () => [{ value: '', label: 'Not a plus-one' }, ...others.filter(g => !g.party_of).map(g => ({ value: g.id, label: `${g.name} (${recordId('GS', g.id)})` }))],
    [others],
  );
  const sideOptions = useMemo(() => {
    const present = buildSideOptions([...others, ...(guest ? [guest] : [])]).filter(o => o.value !== ALL_SIDES && o.value !== NO_SIDE);
    const current = form.side.trim().toLowerCase();
    const known = present.some(o => o.value === current);
    return [
      { value: '', label: 'No side' },
      ...(current && !known ? [{ value: current, label: form.side }] : []),
      ...present,
      { value: NEW_SIDE, label: 'Another side…' },
    ];
  }, [others, guest, form.side]);

  const tagOptions = tagCatalog;
  const tagColors = useMemo(() => Object.fromEntries(tagOptions.map(t => [t.label, t.color])), [tagOptions]);
  const knownTagLabels = useMemo(() => Array.from(new Set([...tagOptions.map(t => t.label), ...form.tags.map(t => t.label)])), [tagOptions, form.tags]);

  const onTagLabels = (labels: string[]) => {
    const byLabel = new Map<string, GuestTag>([...tagOptions, ...form.tags].map(t => [t.label, t]));
    set('tags', labels.map(label => byLabel.get(label)).filter((t): t is GuestTag => Boolean(t)));
  };

  const addPlusOne = () => {
    const name = normalizeGuestName(newPlusOneName).trim();
    if (!name) return;
    set('plus_ones', [...form.plus_ones, { id: idForPlusOne(), name, relationship: DEFAULT_PLUS_ONE_RELATIONSHIP }]);
    setNewPlusOneName('');
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const name = normalizeGuestName(form.name).trim();
    if (!name) { setErrors({ name: 'Name is required.' }); return; }
    const side = form.side === NEW_SIDE ? customSide.trim().toLowerCase() : form.side.trim().toLowerCase();
    const payload = {
      name,
      email: form.email.trim(),
      phone: form.phone.trim(),
      side: side || null,
      role: form.role || null,
      is_honoree: form.is_honoree,
      tags: form.tags,
      rsvp_status: form.rsvp_status,
      status: form.status,
      dietary_restrictions: form.dietary_restrictions,
      notes: form.notes.trim() ? form.notes : null,
      plus_ones: form.plus_ones,
      party_of: form.party_of || null,
      party_role: form.party_of ? (form.party_role || 'plus_one') : null,
    };
    setSaving(true);
    try {
      const saved = guest
        ? await updateGuest(guest.id, payload as unknown as Partial<Guest>)
        : await createGuest({
            ...payload, avatar: '', archived: false,
            notes: payload.notes ?? undefined,
          } as unknown as Omit<Guest, 'id' | 'created_at' | 'updated_at'>);
      if (!saved) throw new Error('Guest not found.');
      onSaved(saved);
    } catch (err) {
      showError(readableErrorMessage(err, 'Failed to save the guest. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="gst-form" onSubmit={submit} noValidate aria-label={isEditing ? 'Edit guest' : 'New guest'}>
      <div className="form-row">
        <FormInput
          label="Name" required value={form.name} error={errors.name} placeholder="Full name"
          onChange={e => set('name', e.target.value)}
          onBlur={() => set('name', normalizeGuestName(form.name))}
        />
        <FormSelect label="RSVP" value={form.rsvp_status} options={RSVP_OPTIONS}
          onChange={e => set('rsvp_status', e.target.value as RSVPStatus)} />
      </div>

      <div className="form-row">
        <div className="gst-form__field">
          <FormInput label="Email" type="email" value={form.email} placeholder="name@example.com"
            onChange={e => set('email', e.target.value)} onBlur={emailWarning.onBlur} />
          <DuplicateContactWarning warning={emailWarning.warning} onDismiss={emailWarning.dismiss} />
        </div>
        <div className="gst-form__field">
          <FormInput label="Phone" type="tel" value={form.phone} placeholder={PHONE_HINT}
            onChange={e => set('phone', e.target.value)} onBlur={phoneWarning.onBlur} />
          <DuplicateContactWarning warning={phoneWarning.warning} onDismiss={phoneWarning.dismiss} />
        </div>
      </div>

      <div className="form-row">
        <FormSelect label="Side" value={form.side} options={sideOptions} onChange={e => set('side', e.target.value)} />
        {form.side === NEW_SIDE && (
          <FormInput label="Side name" value={customSide} placeholder="e.g. osim" onChange={e => setCustomSide(e.target.value)} />
        )}
        <FormSelect label="Role at the event" value={form.role} options={GUEST_ROLE_OPTIONS.map(o => ({ ...o }))} onChange={e => set('role', e.target.value)} />
        <FormSelect label="Status" value={form.status}
          options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]}
          onChange={e => set('status', e.target.value as GuestStatus)} />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Tags</label>
          <TagMultiSelect values={form.tags.map(t => t.label)} options={knownTagLabels} optionColors={tagColors}
            allowCreate={false} placeholder="VVIP, VIP, General, Military…" onChange={onTagLabels} />
        </div>
        <div className="form-group">
          <label className="form-label">Dietary needs</label>
          <TagMultiSelect values={form.dietary_restrictions} options={DIETARY_OPTIONS} placeholder="Add…"
            onChange={values => set('dietary_restrictions', values)} />
        </div>
      </div>

      <FormCheckbox checked={form.is_honoree} onChange={e => set('is_honoree', e.target.checked)}>
        This guest is an honoree (one of the people being celebrated)
      </FormCheckbox>

      <fieldset className="gst-form__group">
        <legend>Plus-ones</legend>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">This guest is a plus-one of</label>
            <SearchableSelect value={form.party_of} options={partyOfOptions} placeholder="Not a plus-one"
              onChange={value => set('party_of', value)} />
          </div>
        </div>
        {form.plus_ones.length > 0 && (
          <ul className="gst-plusones" aria-label="Plus-ones">
            {form.plus_ones.map(p => (
              <li key={p.id}>
                <span>{p.name || 'Unnamed plus-one'}</span>
                <button type="button" className="btn-icon" title="Remove this plus-one" aria-label={`Remove ${p.name || 'plus-one'}`}
                  onClick={() => set('plus_ones', form.plus_ones.filter(x => x.id !== p.id))}>
                  <Icon name="trash" size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="gst-plusones__add">
          <FormInput label="Add a plus-one by name" value={newPlusOneName} placeholder="Plus-one's name"
            onChange={e => setNewPlusOneName(e.target.value)}
            onBlur={() => setNewPlusOneName(normalizeGuestName(newPlusOneName))}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addPlusOne(); } }} />
          <Button type="button" variant="outline" size="sm" onClick={addPlusOne} disabled={!newPlusOneName.trim()}>Add</Button>
        </div>
      </fieldset>

      {guest && <GuestRelationsPanel guest={guest} editable />}

      <FormTextarea label="Notes" rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} />

      <div className="gst-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button type="submit" variant="primary" loading={saving}>{isEditing ? 'Save changes' : 'Add guest'}</Button>
      </div>
    </form>
  );
};

export default GuestForm;
